import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export const RUBRIC_KEY = ["top-shop", "rubric"];

interface Criterion { id: string; focus_area_id: string; name: string; min_points: number; max_points: number; sort_order: number }
interface Area { id: string; name: string; sort_order: number }
type Entry = { points: string; notes: string };

function fmt(n: number) { return Number.isInteger(n) ? String(n) : n.toFixed(1); }

/** Loads active focus areas and active criteria for the top-shop program. */
export function useRubric() {
  return useQuery({
    queryKey: [...RUBRIC_KEY, "definition"],
    queryFn: async () => {
      const { data: prog, error: pErr } = await supabase.from("programs").select("id").eq("slug", "top-shop").maybeSingle();
      if (pErr) throw pErr;
      if (!prog) return { areas: [] as Area[], criteria: [] as Criterion[] };
      const { data: areas, error: aErr } = await supabase.from("program_focus_areas")
        .select("id, name, sort_order").eq("program_id", prog.id).eq("is_active", true).order("sort_order");
      if (aErr) throw aErr;
      const ids = (areas ?? []).map((a) => a.id);
      const { data: criteria, error: cErr } = ids.length
        ? await supabase.from("program_challenge_criteria").select("id, focus_area_id, name, min_points, max_points, sort_order")
            .in("focus_area_id", ids).eq("is_active", true).order("sort_order")
        : { data: [], error: null };
      if (cErr) throw cErr;
      return { areas: (areas ?? []) as Area[], criteria: (criteria ?? []) as Criterion[] };
    },
  });
}

export function RubricScorer({ applicationId, focusAreaId }: { applicationId: string; focusAreaId?: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: rubric, isLoading } = useRubric();
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [saving, setSaving] = useState(false);

  const { data: existing, isLoading: scoresLoading } = useQuery({
    queryKey: [...RUBRIC_KEY, "scores", applicationId],
    queryFn: async () => {
      const { data, error } = await supabase.from("program_criterion_scores")
        .select("criterion_id, points, notes").eq("application_id", applicationId);
      if (error) throw error;
      return data ?? [];
    },
  });

  useEffect(() => {
    const m: Record<string, Entry> = {};
    (existing ?? []).forEach((s) => { m[s.criterion_id] = { points: String(Number(s.points)), notes: s.notes ?? "" }; });
    setEntries(m);
  }, [existing]);

  const areas = useMemo(() => (rubric?.areas ?? []).filter((a) => !focusAreaId || a.id === focusAreaId), [rubric, focusAreaId]);
  const criteria = useMemo(() => (rubric?.criteria ?? []).filter((c) => areas.some((a) => a.id === c.focus_area_id)), [rubric, areas]);

  const value = (c: Criterion) => {
    const raw = (entries[c.id]?.points ?? "").trim();
    const n = Number(raw);
    return raw !== "" && !Number.isNaN(n) ? n : 0;
  };

  const save = async () => {
    const upserts: any[] = [];
    const deletes: string[] = [];
    const existingIds = new Set((existing ?? []).map((e) => e.criterion_id));
    for (const c of criteria) {
      const raw = (entries[c.id]?.points ?? "").trim();
      if (raw === "") { if (existingIds.has(c.id)) deletes.push(c.id); continue; }
      const n = Number(raw);
      if (Number.isNaN(n) || n < c.min_points || n > c.max_points) {
        toast.error(`${c.name}: points must be between ${c.min_points} and ${c.max_points}`);
        return;
      }
      upserts.push({
        application_id: applicationId, criterion_id: c.id, points: n,
        notes: entries[c.id]?.notes?.trim() || null,
        scored_by: user?.id ?? null, scored_at: new Date().toISOString(),
      });
    }
    setSaving(true);
    try {
      if (upserts.length) {
        const { error } = await supabase.from("program_criterion_scores").upsert(upserts, { onConflict: "application_id,criterion_id" });
        if (error) throw error;
      }
      if (deletes.length) {
        const { error } = await supabase.from("program_criterion_scores").delete().eq("application_id", applicationId).in("criterion_id", deletes);
        if (error) throw error;
      }
      toast.success("Scores saved");
      qc.invalidateQueries({ queryKey: RUBRIC_KEY });
      qc.invalidateQueries({ queryKey: ["admin", "top-shop"] });
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save scores");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || scoresLoading) return <Loader2 className="h-5 w-5 animate-spin" />;
  if (!areas.length) return <p className="text-sm text-muted-foreground">No active scoring areas.</p>;

  const renderArea = (a: Area) => {
    const list = criteria.filter((c) => c.focus_area_id === a.id);
    const sub = list.reduce((s, c) => s + value(c), 0);
    const max = list.reduce((s, c) => s + c.max_points, 0);
    const rows = (
      <div className="space-y-3">
        {list.length === 0 && <p className="text-sm text-muted-foreground">No active criteria.</p>}
        {list.map((c) => (
          <div key={c.id} className="grid gap-2 sm:grid-cols-[1fr_auto]">
            <div>
              <label htmlFor={`crit-${applicationId}-${c.id}`} className="text-sm font-medium">{c.name}</label>
              <p className="text-xs text-muted-foreground">{c.min_points} to {c.max_points}</p>
            </div>
            <Input id={`crit-${applicationId}-${c.id}`} type="number" step={0.5} min={c.min_points} max={c.max_points}
              className="w-24" value={entries[c.id]?.points ?? ""}
              onChange={(e) => setEntries((s) => ({ ...s, [c.id]: { points: e.target.value, notes: s[c.id]?.notes ?? "" } }))} />
            <Input aria-label={`Notes for ${c.name}`} placeholder="Notes (optional)" className="sm:col-span-2"
              value={entries[c.id]?.notes ?? ""}
              onChange={(e) => setEntries((s) => ({ ...s, [c.id]: { points: s[c.id]?.points ?? "", notes: e.target.value } }))} />
          </div>
        ))}
      </div>
    );
    const header = <span className="text-sm">Subtotal: <strong>{fmt(sub)}</strong> of {max}</span>;
    if (focusAreaId) {
      return <div key={a.id} className="space-y-3"><div className="flex justify-between"><span className="font-medium">{a.name}</span>{header}</div>{rows}</div>;
    }
    return (
      <Collapsible key={a.id} className="rounded-md border p-3">
        <CollapsibleTrigger className="group flex w-full items-center justify-between gap-2 text-left">
          <span className="flex items-center gap-1 font-medium"><ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />{a.name}</span>
          {header}
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3">{rows}</CollapsibleContent>
      </Collapsible>
    );
  };

  return (
    <div className="space-y-3">
      {areas.map(renderArea)}
      <Button size="sm" onClick={save} disabled={saving}>
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save scores
      </Button>
    </div>
  );
}

/** For the instructor review page: finds the reviewed user's application and the focus area linked to the module. */
export function TopShopSubmissionScorePanel({ userId, moduleId }: { userId?: string; moduleId?: string }) {
  const { data } = useQuery({
    queryKey: [...RUBRIC_KEY, "submission-panel", userId, moduleId],
    enabled: !!userId && !!moduleId,
    queryFn: async () => {
      const { data: area } = await supabase.from("program_focus_areas").select("id, program_id").eq("module_id", moduleId!).maybeSingle();
      if (!area) return null;
      const { data: appId, error } = await supabase.rpc("get_top_shop_application_id", { p_user_id: userId! });
      if (error || !appId) return null;
      return { applicationId: appId as string, focusAreaId: area.id as string };
    },
  });
  if (!data) return null;
  return (
    <div className="rounded-lg border bg-card p-6 space-y-4">
      <h2 className="text-lg font-semibold">Top Shop score</h2>
      <RubricScorer applicationId={data.applicationId} focusAreaId={data.focusAreaId} />
    </div>
  );
}
